import { useEffect, useMemo } from 'react';

import { useModelLoader } from '@spazious/dollhouse-engine';
import { useGetFCompositionSuspenseQuery } from '@spazious/dollhouse-graphql/apollo';
import { useForceUpdate } from '@spazious/react-hooks';

import { FEnsemble } from '../fensembles/index.js';
import { useFurnitureResolver } from '../hooks/index.js';
import { Schema } from '../schema/index.js';

export const createHookInstance =
  <TInstance extends FEnsemble>(factory: (id: number, schema: Schema) => TInstance) =>
  (id: number, attrs: Record<string, any>): TInstance => {
    const forceUpdate = useForceUpdate();

    // Resolve schema for the given type
    const {
      data: {
        fcomposition: { schema },
      },
    } = useGetFCompositionSuspenseQuery({
      variables: { id: id as any },
    });

    // Create an instance of FComposition depending on the type
    const instance = useMemo(() => factory(id, schema), [id, schema]) as unknown as TInstance;

    // Resolve node types for the given instance
    const nodeTypes = useMemo(
      () => {
        instance.setValues(attrs ?? {});
        return instance.getNodeTypes();
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [attrs, instance]
    );

    // Resolve furniture models for the given node types
    const furnitures = useFurnitureResolver(nodeTypes);
    const furnitureModels = useModelLoader(furnitures.map(({ url }) => url));

    // Set furniture models to the instance
    useEffect(() => {
      instance.setObjects(furnitureModels.map(({ scene }) => scene));
      forceUpdate();
    }, [instance, forceUpdate, furnitureModels]);

    return instance;
  };
